import { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, CalendarPlus, Check, Mail, QrCode, Sparkles, Tag, Webhook } from 'lucide-react';
import { Sparkline } from '@/components/cards/Sparkline';
import { CtaLink, Headline, Kicker, Reveal, Section, Spec, Tile } from '../ui';
import { cn } from '@/lib/utils';
import { JourneyStory } from '../ScrollStory';

const SERIES = [3, 5, 4, 8, 6, 9, 12, 10, 14, 11, 16, 15, 19, 17, 22, 20, 24, 21, 27, 25, 30, 28, 33, 31, 35, 34, 38, 36, 41, 44];

export function GrowPage() {
  const [delay, setDelay] = useState(24);

  return (
    <>
      <Section className="play-subhero play-subhero--grow pb-20 pt-16">
        <Reveal className="max-w-3xl">
          <Kicker>Grow · good timing on autopilot</Kicker>
          <h1 className="play-subhero__title mt-4">
            The follow-ups you'd forget, <em>sent anyway.</em>
          </h1>
          <p className="play-subhero__body mt-6 max-w-xl">
            Automations that email a new lead tomorrow. Events people can add to their calendar in
            one tap. A tracking tag per conference so you know which one paid off. Analytics that
            answer "did anyone actually look?"
          </p>
        </Reveal>

        {/* Automation timeline */}
        <Reveal delay={0.1} className="play-automation mt-14 rounded-3xl p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="mk-kicker text-ink-muted">Automation · lead_created</p>
              <p className="mt-1 text-lg font-medium tracking-tight text-ink">"Thanks for stopping by" email</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="mk-kicker text-ink-muted">send after</span>
              {[1, 24, 72].map((h) => (
                <button key={h} onClick={() => setDelay(h)} aria-pressed={delay === h} className={cn('h-8 rounded-full border px-3 font-mono text-[10px] uppercase tracking-widest', delay === h ? 'border-ink bg-ink text-bone' : 'border-ink/20 text-ink-muted')}>
                  {h}h
                </button>
              ))}
            </div>
          </div>
          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            {[
              { t: 'Exchange', d: 'Jordan taps Exchange on your card.', when: 'now' },
              { t: 'Scheduled', d: 'Haphe queues the email with Resend, merge fields filled.', when: 'now' },
              { t: 'Delivered', d: `“Hi Jordan, thanks for stopping by…” from your reply-to.`, when: `+${delay}h` },
            ].map((s, i) => (
              <motion.div key={s.t} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.12 }} className="play-automation__node rounded-2xl p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-ink">{s.t}</span>
                  <span className="font-mono text-[10px] text-accent">{s.when}</span>
                </div>
                <p className="mt-1 text-sm text-ink-muted">{s.d}</p>
              </motion.div>
            ))}
          </div>
          <p className="mt-4 text-xs text-ink-muted">
            Filter by tag so the conference drip only goes to conference leads. Merge fields: name,
            first name, your name, company, card link.
          </p>
        </Reveal>
      </Section>

      <JourneyStory kind="grow" />
      <Section className="play-page-section py-24">
        <div className="grid gap-4 md:grid-cols-2">
          <Reveal>
            <Tile kicker="Events" title="Add to calendar, one tap" body="List an event on your card: a market, a talk, office hours. Weekly, monthly or yearly repeats. Visitors get an .ics that lands in Apple, Google or Outlook with your name on it.">
              <div className="flex items-center gap-2 rounded-xl border border-bone-deep bg-bone p-3 text-sm">
                <CalendarPlus className="h-4 w-4 text-accent" />
                <span>Heights Market · Sat 9:00 · weekly</span>
              </div>
            </Tile>
          </Reveal>
          <Reveal delay={0.05}>
            <Tile kicker="Tracking tags" title="One card, many doors" body="Make a tag per event, per poster, per email signature. Each gets its own QR and link (?t=conference). Leads carry the tag so you know which door they came through.">
              <div className="flex flex-wrap gap-1.5">
                {['sxsw', 'newsletter', 'window-poster', 'podcast'].map((t) => (
                  <span key={t} className="flex items-center gap-1 rounded-full bg-bone-warm px-2.5 py-1 font-mono text-[10px] text-ink-soft"><Tag className="h-3 w-3" />{t}</span>
                ))}
              </div>
            </Tile>
          </Reveal>
          <Reveal delay={0.1}>
            <Tile kicker="Analytics" title="Views, taps, funnel, return visits" body="Per card: a 30-day view trend, which links get tapped, save-contact and exchange rates, and the shop funnel from add-to-cart to paid.">
              <div className="rounded-xl border border-bone-deep bg-bone p-3">
                <div className="flex items-baseline justify-between"><span className="mk-kicker text-ink-muted">30 days</span><span className="text-sm font-medium">612 views</span></div>
                <Sparkline data={SERIES} className="mt-1 text-accent" height={36} ariaLabel="Sample views" />
              </div>
            </Tile>
          </Reveal>
          <Reveal delay={0.15}>
            <Tile kicker="Integrations" title="Webhooks to anything" body="Every lead POSTs to a URL you choose, signed with a per-card secret. Zapier, Make, n8n, HubSpot, Pipedrive, a Google Sheet.">
              <div className="flex items-center gap-2 text-xs text-ink-muted"><Webhook className="h-4 w-4 text-accent" /> x-haphe-signature: sha256=…</div>
            </Tile>
          </Reveal>
        </div>
      </Section>

      <Section dark className="play-page-dark py-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <Kicker gold>AI, in its place</Kicker>
            <Headline className="mt-4 text-bone">Three small jobs. None of them the headline.</Headline>
            <p className="mt-5 max-w-md text-base leading-relaxed text-bone/65">
              We don't sell "AI-powered". We use it where it saves you a blank page: writing a bio
              from your title and company, turning a selfie into a studio headshot, and drafting the
              first reply to a lead. You always edit, you always send.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="grid gap-3">
            <Spec dark title="AI bio" rows={[['input', 'name, title, company, notes'], ['output', 'three options, your pick'], ['limit', '300 characters, on-brand']]} />
            <Spec dark title="AI headshot" rows={[['input', 'a selfie'], ['presets', 'studio, outdoor, editorial…'], ['output', 'square, ready for the card']]} />
            <Spec dark title="Reply draft" rows={[['input', 'the lead’s note + your card'], ['output', 'a first reply in your voice']]} />
          </Reveal>
        </div>
        <Reveal className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-bone/60">
          <span className="flex items-center gap-2"><Sparkles className="h-4 w-4 mk-gold" /> Nothing is posted or sent by AI.</span>
          <span className="flex items-center gap-2"><Mail className="h-4 w-4 mk-gold" /> Emails go out from your reply-to.</span>
          <span className="flex items-center gap-2"><BarChart3 className="h-4 w-4 mk-gold" /> Usage is capped per day so it stays affordable.</span>
        </Reveal>
      </Section>

      <Section className="play-page-final py-24 text-center">
        <Reveal>
          <Headline size="xl" className="mx-auto max-w-3xl">Let the card do the follow-up.</Headline>
          <p className="mx-auto mt-6 max-w-md text-base text-ink-muted">
            <Check className="mr-1 inline h-4 w-4 text-accent" />
            Automations, events, tags, analytics and integrations are all in Pro.
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <CtaLink to="/pricing">See plans</CtaLink>
            <CtaLink to="/signup" variant="ghost"><QrCode className="h-4 w-4" /> Get your card, free</CtaLink>
          </div>
        </Reveal>
      </Section>
    </>
  );
}
